(() => {
  const root = window.Shopify?.routes?.root || '/';
  const builder = document.querySelector('[data-bcos-bundle-builder]');
  if (!builder) return;

  const type = builder.dataset.bundleType || 'mix_match';
  const discountType = builder.dataset.discountType || 'none';
  const discountValue = parseFloat(builder.dataset.discountValue || '0');
  const isByok = type === 'byok';
  const messages = {
    max: builder.dataset.msgMax || 'You can only select up to __COUNT__ products in this step',
    complete: builder.dataset.msgComplete || 'Please complete every required step',
    failed: builder.dataset.msgFailed || 'Unable to add this kit to your cart. Please try again.',
  };

  const steps = [...builder.querySelectorAll('[data-bundle-step]')];
  const progressFill = builder.querySelector('[data-bundle-progress-fill]');
  const progressLabel = builder.querySelector('[data-bundle-progress-label]');
  const progressBar = builder.querySelector('[role="progressbar"]');
  const summaryList = builder.querySelector('[data-bundle-summary-list]');
  const subtotalEl = builder.querySelector('[data-bundle-subtotal]');
  const savingsRow = builder.querySelector('[data-bundle-savings]');
  const savingsEl = builder.querySelector('[data-bundle-savings-amount]');
  const totalEl = builder.querySelector('[data-bundle-total]');
  const errorEl = builder.querySelector('[data-bundle-error]');
  const submitButton = builder.querySelector('[data-bundle-builder-submit]');
  const prevButton = builder.querySelector('[data-bundle-prev]');
  const nextButton = builder.querySelector('[data-bundle-next]');

  function track(name, data) {
    if (window.BcosAnalytics && typeof window.BcosAnalytics.track === 'function') {
      window.BcosAnalytics.track(name, data || {}, 'analytics');
    }
  }

  function formatMoney(cents) {
    return window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(cents, window.Shopify.moneyFormat)
      : new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function stepState(stepEl) {
    return {
      el: stepEl,
      min: parseInt(stepEl.dataset.stepMin, 10) || 1,
      max: parseInt(stepEl.dataset.stepMax, 10) || 1,
      required: stepEl.dataset.stepRequired !== 'false',
    };
  }

  const state = steps.map(stepState);

  function selectionsFor(stepIndex) {
    const step = steps[stepIndex];
    return [...step.querySelectorAll('[data-bundle-select]:checked')];
  }

  function selectedVariants() {
    const items = [];
    steps.forEach((step, index) => {
      selectionsFor(index).forEach((input) => {
        const option = input.closest('[data-bundle-option]');
        const variantSelect = option.querySelector('[data-bundle-variant-select]');
        const selectedOption = variantSelect?.selectedOptions?.[0];
        const price = parseInt(selectedOption?.dataset.price || option.dataset.price, 10) || 0;
        const compareAt = parseInt(selectedOption?.dataset.compareAt || option.dataset.compareAt, 10) || price;
        items.push({
          variantId: (selectedOption || input).value,
          price,
          compareAt,
          title: option.querySelector('[data-bundle-option-title]')?.textContent?.trim() || '',
          stepIndex: index,
        });
      });
    });
    return items;
  }

  function stepCount(stepIndex) {
    return selectionsFor(stepIndex).length;
  }

  function isStepValid(stepIndex) {
    const config = state[stepIndex];
    const count = stepCount(stepIndex);
    if (!config.required) return true;
    return count >= config.min;
  }

  function allValid() {
    return state.every((_, index) => isStepValid(index));
  }

  function setError(message) {
    if (errorEl) errorEl.textContent = message || '';
  }

  function updateProgress() {
    if (!progressFill || !progressLabel) return;

    if (isByok) {
      const current = steps.findIndex((step) => !step.classList.contains('bcos-bundle-builder__step--hidden'));
      const active = current === -1 ? 0 : current;
      const pct = Math.round(((active + 1) / steps.length) * 100);
      progressFill.style.width = `${pct}%`;
      progressLabel.textContent = `Step ${active + 1} of ${steps.length}`;
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(pct));
      return;
    }

    const totalMin = state.reduce((sum, config) => sum + (config.required ? config.min : 0), 0);
    const totalSelected = state.reduce((sum, config, index) => sum + stepCount(index), 0);
    const completed = state.reduce((sum, config, index) => sum + (isStepValid(index) ? 1 : 0), 0);
    const pct = totalMin
      ? Math.min(100, Math.round((totalSelected / totalMin) * 100))
      : completed === steps.length ? 100 : 0;
    progressFill.style.width = `${pct}%`;
    progressLabel.textContent = `${completed} of ${steps.length} complete`;
    if (progressBar) progressBar.setAttribute('aria-valuenow', String(pct));
  }

  function updateStepCounts() {
    steps.forEach((step, index) => {
      const count = step.querySelector('[data-bundle-step-count]');
      if (!count) return;
      const config = state[index];
      count.textContent = config.required
        ? `${stepCount(index)} / ${config.min} required`
        : `${stepCount(index)} selected`;
      count.classList.toggle('bcos-bundle-builder__step-count--valid', isStepValid(index));
    });
  }

  function updateSummary() {
    const items = selectedVariants();
    const subtotal = items.reduce((sum, item) => sum + item.price, 0);
    const compareSubtotal = items.reduce((sum, item) => sum + item.compareAt, 0);
    const compareSavings = Math.max(0, compareSubtotal - subtotal);

    let bundleDiscount = 0;
    if (discountType === 'percent' && discountValue > 0) {
      bundleDiscount = Math.round(subtotal * (discountValue / 100));
    } else if (discountType === 'amount' && discountValue > 0) {
      bundleDiscount = Math.min(subtotal, Math.round(discountValue * 100));
    }

    const total = subtotal - bundleDiscount;
    const totalSavings = compareSavings + bundleDiscount;

    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    if (totalEl) totalEl.textContent = formatMoney(total);

    if (savingsRow) {
      savingsRow.hidden = totalSavings <= 0;
      if (savingsEl) savingsEl.textContent = formatMoney(totalSavings);
    }

    if (summaryList) {
      summaryList.innerHTML = items.length
        ? items.map((item) => `
            <li class="bcos-bundle-builder__summary-item">
              <span>
                <span class="bcos-bundle-builder__summary-item-name">${escapeHtml(item.title)}</span>
                <button type="button" class="bcos-bundle-builder__summary-item-remove" data-bundle-summary-remove="${item.variantId}" aria-label="Remove ${escapeHtml(item.title)}">
                  ${renderIcon('remove')}
                </button>
              </span>
              <span>${formatMoney(item.price)}</span>
            </li>`).join('')
        : '<li class="bcos-bundle-builder__summary-empty">No products selected yet</li>';
    }

    const valid = allValid();
    if (submitButton) submitButton.disabled = !valid;
    if (prevButton) prevButton.disabled = !isByok;
    if (nextButton && isByok) {
      const current = steps.findIndex((step) => !step.classList.contains('bcos-bundle-builder__step--hidden'));
      nextButton.disabled = current === steps.length - 1;
    }
    setError('');
    updateProgress();
    updateStepCounts();
  }

  function renderIcon(name) {
    const icons = {
      remove: '<svg class="bcos-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18"/></svg>',
    };
    return icons[name] || '';
  }

  function openCart() {
    const toggle = document.querySelector('[data-bcos-cart-toggle]');
    if (toggle) {
      toggle.click();
    } else {
      document.dispatchEvent(new CustomEvent('bcos:cart:updated'));
    }
  }

  builder.addEventListener('change', (event) => {
    const input = event.target.closest('[data-bundle-select]');
    const select = event.target.closest('[data-bundle-variant-select]');
    if (input) {
      const option = input.closest('[data-bundle-option]');
      const stepIndex = parseInt(option.closest('[data-bundle-step]').dataset.stepIndex, 10);
      const max = state[stepIndex].max;
      const checked = input.checked;
      if (checked && stepCount(stepIndex) > max) {
        input.checked = false;
        setError(messages.max.replace('__COUNT__', String(max)));
        updateSummary();
        return;
      }
      const variantSelect = option.querySelector('[data-bundle-variant-select]');
      if (variantSelect) variantSelect.disabled = !checked;
      updateSummary();
      track(checked ? 'bundle_product_added' : 'bundle_product_removed', {
        product_id: option.dataset.productId,
        variant_id: input.value,
        step: stepIndex + 1,
      });
      return;
    }
    if (select) {
      const variantId = select.value;
      const checkbox = select.closest('[data-bundle-option]').querySelector('[data-bundle-select]');
      if (checkbox) {
        checkbox.value = variantId;
      }
      updateSummary();
    }
  });

  summaryList?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-bundle-summary-remove]');
    if (!button) return;
    const variantId = button.dataset.bundleSummaryRemove;
    const input = builder.querySelector(`[data-bundle-select][value="${variantId}"]`);
    if (input) {
      input.checked = false;
      updateSummary();
      const option = input.closest('[data-bundle-option]');
      track('bundle_product_removed', { product_id: option.dataset.productId, variant_id: variantId });
    }
  });

  if (isByok) {
    function visibleStepIndex() {
      return steps.findIndex((step) => !step.classList.contains('bcos-bundle-builder__step--hidden'));
    }

    function showStep(index) {
      steps.forEach((step, i) => {
        step.classList.toggle('bcos-bundle-builder__step--hidden', i !== index);
      });
      const next = steps[index].querySelector('.bcos-bundle-option__select input');
      if (next && next.type !== 'hidden') next.focus({ preventScroll: true });
      updateSummary();
      track('bundle_step_completed', { step: index + 1, total: steps.length });
    }

    prevButton?.addEventListener('click', () => {
      const current = visibleStepIndex();
      if (current > 0) showStep(current - 1);
    });

    nextButton?.addEventListener('click', () => {
      const current = visibleStepIndex();
      if (current < steps.length - 1) showStep(current + 1);
    });
  }

  builder.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!allValid()) {
      setError(messages.complete);
      return;
    }

    const button = submitButton;
    if (button) button.disabled = true;

    const items = selectedVariants();
    const payload = items.map((item) => ({ id: item.variantId, quantity: 1 }));

    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Error');
      track('bundle_added_to_cart', {
        items: payload,
        value: items.reduce((sum, item) => sum + item.price, 0) / 100,
      });
      openCart();
    } catch (error) {
      console.error('Bundle add to cart failed', error);
      setError(messages.failed);
      if (button) button.disabled = false;
    }
  });

  updateSummary();

  track('bundle_started', { type });
})();
